import { useEffect, useState } from "react";
import { AcceptanceCheckboxes } from "src/apps/components/checkbox/AcceptanceCheckboxes";
import { useGetConsents } from "src/apps/hooks/useGetAcceptanceCheckboxes";
import { LoadingScreen } from "src/apps/components/loading/LoadingScreen";
import { BoldHeader1, Text } from "src/apps/components/typography/Texts";
import { Icon, ICON_SIZES } from "src/apps/components/icon/Icon";

import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { useTranslations } from "src/apps/hooks/useTranslations";
import { T } from "src/apps/components/i18n/Translation";
import { Button } from "src/apps/components/button/Button";
import { useCreateSignedAcceptances } from "src/apps/hooks/useCreateSignedAcceptances";
import { FormContainer } from "./FormContainer";
import { useTheme } from "src/apps/providers/ThemeStore";
import { ImArrowRight2 } from "react-icons/im";
import {
  AnalyticsEvents,
  useSendAnalyticEvent,
} from "src/apps/hooks/analytics/useSendAnalyticEvent";
import { colors } from "@permisso/permisso-components";
import { TEST_IDS } from "src/apps/config/testIds";
import { useNavigateToErrorScreen } from "src/apps/hooks/useNavigateToErrorScreen";

const TITLE = "documentation_area.consents_form.title";
const SUBTITLE = "documentation_area.consents_form.subtitle";
const BUTTON_TEXT = "common.continue";

export const ConsentsForm = () => {
  const { t, translateWithFormat } = useTranslations();
  const { companyName } = useTheme();

  const { acceptanceCheckboxes = [], isGetConsentsLoading, error } = useGetConsents();
  const navigateToErrorScreen = useNavigateToErrorScreen();
  const [checkboxesStatus, setCheckboxesStatus] = useState<boolean[]>(
    Array.from({ length: acceptanceCheckboxes.length }, () => false),
  );
  const { createSignedAcceptances, createSignedAcceptancesPending } = useCreateSignedAcceptances();
  const { sendEvent } = useSendAnalyticEvent();

  useEffect(() => {
    if (acceptanceCheckboxes.length !== checkboxesStatus.length) {
      const checkboxStat = Array.from({ length: acceptanceCheckboxes.length }, () => false);
      setCheckboxesStatus(checkboxStat);
    }
  }, [acceptanceCheckboxes]);

  useEffect(() => {
    if (error) {
      navigateToErrorScreen(error);
    }
  }, [error]);

  useEffect(() => {
    sendEvent(AnalyticsEvents.DOCUMENTATION_AREA_CONSENTS_SCREEN_OPENED);
  }, []);

  const onSubmitForm = () => {
    const translatedCheckboxes = acceptanceCheckboxes.map((checkbox) => ({
      text: translateWithFormat(checkbox.text, { companyName }),
    }));
    createSignedAcceptances(translatedCheckboxes);
  };

  const buttonDisabled = !checkboxesStatus.every(Boolean);

  if (isGetConsentsLoading) return <LoadingScreen />;

  return (
    <FormContainer data-testid={TEST_IDS.consents.form}>
      <Icon size={ICON_SIZES.s30} color={colors.grey800} path={ICON_PATHS.FILE_CHECK} />
      <BoldHeader1>
        <T k={TITLE} />
      </BoldHeader1>
      <Text>
        <T k={SUBTITLE} />
      </Text>
      <AcceptanceCheckboxes
        acceptanceCheckboxes={acceptanceCheckboxes}
        checkboxesStatus={checkboxesStatus}
        setAllCheckboxStatus={setCheckboxesStatus}
      />
      <Button
        dataTestId={TEST_IDS.consents.continueButton}
        rightIcon={<ImArrowRight2 />}
        onClick={onSubmitForm}
        isLoading={createSignedAcceptancesPending}
        text={t(BUTTON_TEXT)}
        disabled={buttonDisabled}
      />
    </FormContainer>
  );
};
